'use client'

import type { LucideIcon } from 'lucide-react'
import { CHIP_TONE_CLASS } from './autoDomainTypes'
import { parseDbTimestamp } from '@/lib/dates'

// The small list chip shared by the Domain Candidates list, its detail, and
// the campaign page's sightings widget — in its own file so CampaignDetailPage
// can use it without importing the dashboard (which itself imports
// CampaignDetailPage).
export function Chip({ label, tone, title }: { label: string; tone: keyof typeof CHIP_TONE_CLASS; title?: string }) {
  return (
    <span title={title} className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest whitespace-nowrap ${CHIP_TONE_CLASS[tone]}`}>
      {label}
    </span>
  )
}

// The backoffice's filter pill: round, grey until selected, then in its
// tone. Nothing selected means everything is shown -- the Domain Candidates
// status / verdict rows and the timeline's "Show" row use it, so a filter
// reads the same wherever it is.
export function FilterPill({ active, label, count, onClick, tone, border, muted, icon: Icon, title }: {
  active: boolean
  label: string
  count?: number
  onClick: () => void
  tone: keyof typeof CHIP_TONE_CLASS
  border: string
  muted: string
  icon?: LucideIcon
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={`inline-flex items-center gap-1 rounded-[4px] border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors whitespace-nowrap ${
        active ? `${CHIP_TONE_CLASS[tone]} bg-current/10` : `${border} ${muted} hover:text-signal hover:border-signal`
      }`}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {label}{count != null && <span className="ml-1 opacity-70">{count}</span>}
    </button>
  )
}

// "3d", "2mo", "1y" — registration age reads faster than a date in a table
export function fmtAge(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return null
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000))
  if (days < 1) return 'today'
  if (days < 60) return `${days}d`
  if (days < 730) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}
